import {
  useDeleteApplicantsMutation,
  useGetAllApplicantsQuery,
  useShortlistApplicantsManualMutation,
} from "@/app/features/recruitment/job-applicants/job-applicants.api";
import ConfirmationModal from "@/components/ConfirmationModal";
import CustomTable from "@/components/CustomTable";
import { DISCLOSER_KEYS, FILTER_KEYS } from "@/constants/keys.constants";
import { useAppDiscloser, useAppFilters } from "@/hooks";
import { EyeIcon, StarIcon, TrashIcon, UserIcon } from "@/icons";
import { cn } from "@/lib/cn";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { formatDate, normalizeColumnValue } from "@/utils";
import { Button, Flex, Space, type TableProps } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import ApplicantsDetails from "./_components/Applicants/ApplicantsDetails";

export default function Applicants() {
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job_id");
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const { page, limit, keyword, filters, handlePagination, handleFilter } =
    useAppFilters(FILTER_KEYS.JOB_APPLICANT);

  const { data: applicantsData, isFetching } = useGetAllApplicantsQuery({
    params: {
      page,
      limit,
      ...filters,
      search: keyword,
    },
  });

  const [deleteApplicant, { isLoading: isDeleting }] =
    useDeleteApplicantsMutation();

  const [shortlistManual, { isLoading: isShortlisting }] =
    useShortlistApplicantsManualMutation();

  const [config, handleOpen, handleClose] = useAppDiscloser(
    DISCLOSER_KEYS.JOB_APPLICANT,
  );

  const handleShortlistConfirm = async () => {
    if (!selectedData.length) return;
    try {
      const res = await shortlistManual({
        job_application_ids: selectedData,
      }).unwrap();

      if (res?.success) {
        toast(
          "success",
          res?.message || "Selected applicants shortlisted successfully",
        );
        setSelectedData([]);
        handleClose();
      } else {
        toast("error", res?.message || "Failed to shortlist applicants");
      }
    } catch (err) {
      toast("error", getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (jobId) {
      handleFilter("job_id", jobId);
      params.delete("job_id");
      setParams(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const columns: TableProps<any>["columns"] = [
    {
      width: 200,
      title: "Job Title",
      dataIndex: "job_title",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      width: 200,
      title: "Applicants Name",
      dataIndex: "applicant_name",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: (
        <div>
          <span className="block">Source</span>
          <span className="text-nowrap block"> (Direct / TalentHunt)</span>
        </div>
      ),
      dataIndex: "is_from_talent_hunt",
      align: "center",
      render: (isTalentHunt: boolean) => (
        <Flex justify="center">
          {isTalentHunt ? (
            <div className="relative group scale-90 origin-center">
              <div className="absolute -inset-1 bg-gradient-to-r from-theme-primary/20 to-theme-primary/10 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
              <Flex
                align="center"
                gap={6}
                className="relative bg-theme-primary/10 border border-theme-primary/30 px-3 py-1 rounded-lg backdrop-blur-md"
              >
                <div className="relative flex items-center justify-center">
                  <StarIcon
                    size={14}
                    className="text-theme-primary animate-[spin_3s_linear_infinite]"
                  />
                  <div className="absolute inset-0 bg-theme-primary/20 blur-sm rounded-full animate-pulse"></div>
                </div>
                <span className="text-theme-primary text-[10px] font-black uppercase tracking-[0.15em] drop-shadow-[0_0_8px_rgba(var(--theme-primary-rgb),0.5)]">
                  Hunt
                </span>
              </Flex>
            </div>
          ) : (
            <div className="relative group scale-90 origin-center">
              <div className="absolute -inset-1 bg-gradient-to-r from-theme-gray-700/20 to-theme-gray-600/10 rounded-lg blur opacity-10 group-hover:opacity-25 transition duration-1000 group-hover:duration-200"></div>
              <Flex
                align="center"
                gap={6}
                className="relative bg-theme-gray-800/20 border border-theme-gray-700/50 px-3 py-1 rounded-lg backdrop-blur-md"
              >
                <div className="relative flex items-center justify-center">
                  <UserIcon size={14} className="text-theme-gray-300" />
                </div>
                <span className="text-theme-gray-300 text-[10px] font-black uppercase tracking-[0.15em]">
                  Direct
                </span>
              </Flex>
            </div>
          )}
        </Flex>
      ),
    },
    {
      width: 200,
      align: "center",
      title: "Applied On",
      dataIndex: "applied_on",
      className: "text-nowrap",
      render: (val: string) => formatDate(val, "DD MMM, YYYY - hh:mm A"),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "NID",
      dataIndex: "nid",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Expected Salary",
      dataIndex: "expected_salary",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, 0),
    },
    {
      title: "Stage",
      dataIndex: "stage",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      align: "center",
      title: "Shortlisted",
      dataIndex: "is_cv_sorted",
      render: (isSorted: boolean, record: any) => (
        <Flex justify="center">
          {isSorted ? (
            record?.is_force_shortlisted ? (
              <span className="status-yellow status">Forced Shortlisted</span>
            ) : (
              <span className="status-success status">Auto Shortlisted</span>
            )
          ) : (
            <span className="status-error status">Failed</span>
          )}
        </Flex>
      ),
    },
    {
      width: 150,
      align: "center",
      title: "CV Processing Score",
      dataIndex: "cv_processing_score",
      render: (val) => (
        <span>
          {val === "pending" ? (
            <span className={cn("status", "status status-yellow")}>
              Pending
            </span>
          ) : (
            <div>{val}</div>
          )}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "end",
      render: (_: string, record: any) => (
        <Space size={8}>
          <Button
            size="small"
            className="action-btn"
            icon={<EyeIcon size={20} />}
            onClick={() => handleOpen({ type: "VIEW", data: record })}
          />

          <Button
            size="small"
            className="action-btn"
            icon={<TrashIcon size={20} />}
            onClick={() => handleOpen({ type: "DELETE", data: record })}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={applicantsData?.data?.data || []}
        onPaginate={handlePagination}
        customTitle="Applicants"
        tableRootClassName="border-0 p-0"
        loading={isFetching}
        total={applicantsData?.data?.meta?.total || 0}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as string[]);
          },
          getCheckboxProps: (record) => ({
            disabled: record?.is_cv_sorted === true,
            className: "custom-check",
          }),
          columnWidth: 40,
        }}
        filterSection={
          <>
            <Button
              size="middle"
              onClick={() => handleOpen({ type: "UPDATE" })}
              disabled={!selectedData.length}
              className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
            >
              Shortlist
            </Button>
          </>
        }
      />

      <ApplicantsDetails
        id={config.data?.id}
        open={config.open && config.type === "VIEW"}
        handleClose={handleClose}
      />

      <ConfirmationModal
        open={config.open && config.type === "DELETE"}
        handleClose={handleClose}
        handleSubmit={async () => {
          if (config.data?.id) {
            try {
              const res = await deleteApplicant({
                id: config.data.id,
              }).unwrap();
              const message = getErrorMessage(res);
              if (res?.success === false) {
                toast("error", message || "Failed to delete candidate");
                return;
              }
              toast("success", message || "Candidate deleted successfully");
              handleClose();
            } catch (err) {
              toast("error", getErrorMessage(err));
            }
          }
        }}
        loading={isDeleting}
        title="Delete Applicant"
        subTitle="Are you sure you want to delete this applicant? This action cannot be undone."
        buttonName="Delete"
      />

      <ConfirmationModal
        open={config.open && config.type === "UPDATE"}
        handleClose={handleClose}
        handleSubmit={handleShortlistConfirm}
        loading={isShortlisting}
        title="Shortlist Applicants"
        subTitle={`Are you sure you want to shortlist selected applicant(s)?`}
        buttonName="Shortlist"
      />
    </div>
  );
}
